import { useContext, useState, useEffect } from "react";
import Checklist from "components/checklist";
import FormContainer from "components/form-container";
import ProgressBar from "components/progress-bar";
import {
  useLocation,
  Link,
  useNavigate,
  createSearchParams,
} from "react-router-dom";
import { getQueryParams } from "lib/utils";
import { useTranslation } from "react-i18next";
import { TAKEBACK_METHOD_NAME } from "consts/query-params";
import AuthContext from "contexts/AuthContext";
import { calculateEstimate } from "lib/calculateEstimate";
import { useCreateTakeback } from "api/use-takebacks";
import LoadingSpinner from "components/loading-spinner";

export default function StoreChecklist() {
  const [allCheckedList1, setAllCheckedList1] = useState(false);
  const [allCheckedList2, setAllCheckedList2] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const checklistItems = [
    {
      name: "brickBasedToys",
      label: t("trade-in.checklist.brick_based_toys"),
    },
    {
      name: "bricksSeparated",
      label: t("trade-in.checklist.bricks_separated"),
    },
    {
      name: "removedElectronics",
      label: t("trade-in.checklist.removed_electronics"),
    },
    {
      name: "removedDuplo",
      label: t("trade-in.checklist.removed_duplo"),
    },
    {
      name: "removedNonLego",
      label: t("trade-in.checklist.removed_non_lego"),
    },
  ];

  const checklistItems2 = [
    {
      name: "packingPreparation",
      element: (
        <div>
          I have followed the{" "}
          <Link
            className="text-[#016db8]"
            to={{
              pathname: t("urls.how_it_works"),
              search: `?${new URLSearchParams({ state: queryParams?.state, preselected: queryParams?.[TAKEBACK_METHOD_NAME] === "mail" ? "mail" : "in-store" }).toString()}`,
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            guide on preparing my trade-in package
          </Link>
        </div>
      ),
    },
  ];

  const giftRatio = calculateEstimate(1).giftRatio;

  const targetPath = t("urls.signup_store_confirmation_exists");

  const { mutate: createTakeback, isLoading: isCreatingTakeback } =
    useCreateTakeback({
      onCreate: ({ sys_id }) => {
        const navigateOptions = {
          pathname: targetPath,
          search: createSearchParams({
            ...queryParams,
            takebackId: sys_id,
          }).toString(),
        };
        navigate(navigateOptions);
      },
      onError: () => {
        // Optionally show an error message
      },
    });

  const onSubmit = () => {
    const params = queryParams
      ? createSearchParams(queryParams).toString()
      : "";
    if (user) {
      const stateCode = (queryParams.state || "").toUpperCase();
      const quickRegType = ["NJ", "CO", "AR", "PA"].includes(stateCode)
        ? `Quick Reg ${stateCode}`
        : "Quick Reg";
      const returnType = `in_store_${stateCode.toLowerCase()}`;
      createTakeback({
        uom: t("scale.weight"),
        weight: 0,
        paymentType: "giftCard",
        returnMethod: returnType,
        returnTypeOffered: quickRegType,
        store: returnType,
        originalReplayWeight: queryParams.replayWeight,
        incetiveRatio: giftRatio,
      });
    } else {
      navigate({
        pathname: t("urls.signup_store_email"),
        search: params,
      });
    }
  };

  useEffect(() => {
    if (!user) {
      const params = queryParams
        ? createSearchParams(queryParams).toString()
        : "";
      navigate({
        pathname: t("urls.signup_store_email"),
        search: params,
      });
    }
  }, [user, navigate, t, queryParams]);

  if (isCreatingTakeback) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 88px)",
        }}
      >
        <LoadingSpinner isLoading={"Creating Trade-In..."} />
      </div>
    );
  }

  return (
    <FormContainer
      submitLabel="Create Trade-In"
      onSubmit={onSubmit}
      submitDisabled={!(allCheckedList1 && allCheckedList2)}
    >
      <ProgressBar step={1} stepsNum={3} />
      <div className="flex flex-col gap-large">
        <div className="flex flex-col gap-regular">
          <h2 className="text-primary text-6xl">
            {t("trade-in.checklist.title")}
          </h2>
          <p className="text-tertiary text-2xl">
            {t("trade-in.checklist.description")}
          </p>
        </div>
        <div className="flex flex-col gap-x-large">
          <h3 className="text-2xl text-primary font-bold">
            {t("trade-in.checklist.checklist_title")}
          </h3>
          <Link
            className="text-blue cursor-pointer"
            to={{
              pathname: t("urls.how_it_works"),
              search: `?${new URLSearchParams({ state: queryParams?.state, preselected: queryParams?.[TAKEBACK_METHOD_NAME] === "mail" ? "mail" : "in-store" }).toString()}`,
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {queryParams?.[TAKEBACK_METHOD_NAME] === "mail"
              ? "Read our guide on sending your bricks via mail"
              : "Read our full guide on preparing your trade-in"}
          </Link>
          <Checklist
            setAllChecked={setAllCheckedList1}
            checklistItems={checklistItems}
          />
        </div>
        <div className="flex flex-col gap-x-large">
          <h3 className="text-2xl font-bold">Packing preparation</h3>
          <Checklist
            setAllChecked={setAllCheckedList2}
            checklistItems={checklistItems2}
          />
        </div>
        <div className="mt-8 text-left flex gap-1 items-start">
          <span
            className={`material-symbols-outlined scale-75 text-blue align-bottom block`}
          >
            info
          </span>{" "}
          <div className="text-sm text-gray-500">
            The LEGO® Brick Take Back Program is being operated for and on
            behalf of the LEGO Group by Pentatonic Ltd. See{" "}
            <Link to={t("urls.terms_and_conditions")} className="text-blue">
              Terms & Conditions
            </Link>
            .
          </div>
        </div>
      </div>
    </FormContainer>
  );
}
